import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import TasksPage from "@/pages/TasksPage";
import VisitsPage from "@/pages/VisitsPage";
import BibleStudiesPage from "@/pages/BibleStudiesPage";
import ProspectsPage from "@/pages/ProspectsPage";
import CustomStudiesPage from "@/pages/CustomStudiesPage";
import MessagesPage from "@/pages/MessagesPage";
import AuthDebugPage from "@/pages/AuthDebugPage";
import ConnectionTestPage from "@/pages/ConnectionTestPage";

// Determine backend URL based on current domain
const getCurrentBackendURL = () => {
  const currentOrigin = window.location.origin;
  
  // If accessing via soul-aid.com, use soul-aid.com as backend
  if (currentOrigin.includes('soul-aid.com')) {
    return currentOrigin;
  }
  
  // Otherwise use configured backend URL or fall back to current origin
  return process.env.REACT_APP_BACKEND_URL || currentOrigin;
};

const BACKEND_URL = getCurrentBackendURL();
const API = `${BACKEND_URL}/api`;

// Log the API URL on app load
console.log("=== Soul Aid App Configuration ===");
console.log("Backend URL:", BACKEND_URL);
console.log("API Endpoint:", API);
console.log("Current Origin:", window.location.origin);
console.log("==================================");

// Auth check component
function AuthCheck({ children }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("AuthCheck useEffect - Full URL:", window.location.href);
    console.log("Hash:", window.location.hash);
    console.log("Search:", window.location.search);
    
    // Check for session_id in URL fragment (hash)
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      console.log("Found session_id in hash");
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      console.log("Extracted session_id from hash:", sessionId.substring(0, 10) + "...");
      processSessionId(sessionId);
      return;
    }
    
    // Check for session_id in query parameters (fallback for mobile)
    const searchParams = new URLSearchParams(window.location.search);
    const querySessionId = searchParams.get('session_id');
    if (querySessionId) {
      console.log("Found session_id in query params");
      console.log("Extracted session_id from query:", querySessionId.substring(0, 10) + "...");
      processSessionId(querySessionId);
      return;
    }
    
    console.log("No session_id found, checking existing auth");
    // Check existing session
    checkAuth();
  }, []);

  const processSessionId = async (sessionId) => {
    try {
      console.log("=== Authentication Process Started ===");
      console.log("Session ID:", sessionId.substring(0, 10) + "...");
      console.log("API URL:", API);
      console.log("Full endpoint:", `${API}/auth/session`);
      
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, { 
        withCredentials: true,
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log("✅ Session response received:", response.data);

      if (response.data.success) {
        // Store session token in localStorage for Safari compatibility
        const sessionToken = response.data.user?.session_token;
        console.log("Session token:", sessionToken ? "✅ present" : "❌ missing");
        
        if (sessionToken) {
          localStorage.setItem('session_token', sessionToken);
          // Set default authorization header for all axios requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
          console.log("✅ Session token stored and header set");
        } else {
          console.warn("⚠️ No session token in response");
        }
        
        // Clean URL
        window.history.replaceState(null, "", window.location.pathname);
        setAuthenticated(true);
        setLoading(false);
        toast.success("Welcome! You're now signed in.");
      }
    } catch (error) {
      console.error("❌ Authentication failed");
      console.error("Error type:", error.name);
      console.error("Error message:", error.message);
      console.error("Error code:", error.code);
      console.error("Response status:", error.response?.status);
      console.error("Response data:", error.response?.data);
      console.error("Full error:", error);
      
      let errorMessage = "Network error";
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.detail || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request made but no response
        errorMessage = "No response from server - check your internet connection";
      } else {
        // Request setup error
        errorMessage = error.message;
      }
      
      toast.error(`Authentication failed: ${errorMessage}`, { duration: 8000 });
      setLoading(false);
      navigate("/");
    }
  };

  const checkAuth = async () => {
    try {
      // Check if we have a stored session token (for Safari compatibility)
      const storedToken = localStorage.getItem('session_token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
      
      await axios.get(`${API}/auth/me`, { withCredentials: true });
      setAuthenticated(true);
    } catch (error) {
      setAuthenticated(false);
      // Clear stored token if auth fails
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
      
      if (location.pathname !== "/") {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!authenticated && location.pathname !== "/") {
    return <Navigate to="/" replace />;
  }

  if (authenticated && location.pathname === "/") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public routes - no auth required */}
          <Route path="/auth-debug" element={<AuthDebugPage />} />
          <Route path="/connection-test" element={<ConnectionTestPage />} />
          
          {/* Protected routes - require auth */}
          <Route path="/*" element={
            <AuthCheck>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/visits" element={<VisitsPage />} />
                <Route path="/bible-studies" element={<BibleStudiesPage />} />
                <Route path="/prospects" element={<ProspectsPage />} />
                <Route path="/custom-studies" element={<CustomStudiesPage />} />
                <Route path="/messages" element={<MessagesPage />} />
              </Routes>
            </AuthCheck>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
